import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import { approvalsApiSlice } from '../../../../../slices/approvalsApiSlice';
import { isRecentLocalOrderMutation } from '../../../../../utils/order/orderLocalMutationGuard';
import { socket } from '../../../../../socket';

/**
 * 订单详情 Modal 统一 Footer：左侧刷新当前订单，右侧可放业务按钮
 */
const OrderModalFooter = ({ children, className, onRemoteOrderUpdate }) => {
	const dispatch = useDispatch();
	const { userInfo } = useSelector((state) => state.auth);
	const { refetch, refetchProcessingOrderLists, isOrderFetching, isLoading, isOrderReady, order } = useOrderContext();
	const footerRef = useRef(null);
	const [showGlassShadow, setShowGlassShadow] = useState(false);
	const showBackgroundSync = isOrderReady && isOrderFetching && !isLoading;

	useEffect(() => {
		if (!isOrderReady || !order?._id) return;
		socket.emit('join order', String(order._id));
	}, [isOrderReady, order?._id]);

	useEffect(() => {
		const handleUpdateOrder = (payload) => {
			if (order?._id && isRecentLocalOrderMutation(order._id)) {
				return;
			}

			const sourceUserId = payload?.sourceUserId != null ? String(payload.sourceUserId) : null;
			const selfUserId = userInfo?._id != null ? String(userInfo._id) : null;
			if (sourceUserId && selfUserId && sourceUserId === selfUserId) {
				return;
			}

			if (typeof onRemoteOrderUpdate === 'function') {
				onRemoteOrderUpdate(payload);
				return;
			}
			if (typeof refetch === 'function') {
				refetch();
			}
			dispatch(approvalsApiSlice.util.invalidateTags(['Approval']));
			refetchProcessingOrderLists?.();
		};

		socket.on('update order', handleUpdateOrder);

		return () => {
			socket.off('update order', handleUpdateOrder);
		};
	}, [dispatch, onRemoteOrderUpdate, order?._id, refetch, refetchProcessingOrderLists, userInfo?._id]);

	/** 与 Header 成对：未滚到底时向上软扩散，提示下方还有内容可滑 */
	useEffect(() => {
		const footerEl = footerRef.current;
		if (!footerEl) return undefined;

		const modalContent = footerEl.closest('.modal-content');
		if (!modalContent) return undefined;

		let scrollEl = null;
		let resizeObserver = null;

		const updateGlassShadow = () => {
			if (!scrollEl) {
				setShowGlassShadow(false);
				return;
			}
			const { scrollTop, clientHeight, scrollHeight } = scrollEl;
			setShowGlassShadow(scrollTop + clientHeight < scrollHeight - 1);
		};

		const findScrollEl = () =>
			modalContent.querySelector('.order-fullscreen-modal__scroll .simplebar-content-wrapper');

		const detachResize = () => {
			resizeObserver?.disconnect();
			resizeObserver = null;
		};

		const attach = () => {
			const next = findScrollEl();
			if (!next) return false;
			if (scrollEl === next) {
				updateGlassShadow();
				return true;
			}
			if (scrollEl) {
				scrollEl.removeEventListener('scroll', updateGlassShadow);
				detachResize();
			}
			scrollEl = next;
			scrollEl.addEventListener('scroll', updateGlassShadow, { passive: true });
			if (typeof ResizeObserver !== 'undefined') {
				resizeObserver = new ResizeObserver(updateGlassShadow);
				const contentEl = scrollEl.querySelector('.simplebar-content') || scrollEl;
				resizeObserver.observe(contentEl);
				resizeObserver.observe(scrollEl);
			}
			updateGlassShadow();
			return true;
		};

		const cleanup = () => {
			scrollEl?.removeEventListener('scroll', updateGlassShadow);
			detachResize();
			setShowGlassShadow(false);
		};

		if (attach()) {
			return cleanup;
		}

		const observer = new MutationObserver(() => {
			if (attach()) observer.disconnect();
		});
		observer.observe(modalContent, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
			cleanup();
		};
	}, [isLoading, isOrderReady]);

	return (
		<Modal.Footer
			ref={footerRef}
			className={classNames('border-top py-2 px-x1 order-modal-footer', className, {
				'order-modal-footer--glass-shadow': showGlassShadow,
			})}
		>
			<div className='d-flex align-items-center justify-content-between w-100 gap-2'>
				<div className='d-flex align-items-center gap-2 py-1 px-2 border-start border-3 border-info bg-100 rounded-1 flex-shrink-0' role='note' aria-label='数据同步提示'>
					<FontAwesomeIcon icon='circle-info' className='text-info flex-shrink-0' style={{ fontSize: '0.7rem' }} />
					<span className='fs-10 text-600 mb-0'>{showBackgroundSync ? '正在同步…' : '同步最新数据'}</span>
					<Button
						variant='link'
						size='sm'
						className='d-inline-flex align-items-center gap-1 p-0 fs-10 text-info text-decoration-none flex-shrink-0'
						onClick={() => {
							if (typeof onRemoteOrderUpdate === 'function') {
								onRemoteOrderUpdate({});
								return;
							}
							refetch?.();
						}}
						disabled={isLoading || isOrderFetching}
						title='刷新'
						aria-label='刷新'
					>
						<FontAwesomeIcon icon='arrows-rotate' spin={showBackgroundSync || isOrderFetching} className='fa-fw' />
						刷新
					</Button>
				</div>
				{children ? <div className='d-flex align-items-center flex-wrap gap-2 ms-auto'>{children}</div> : null}
			</div>
		</Modal.Footer>
	);
};

OrderModalFooter.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	onRemoteOrderUpdate: PropTypes.func,
};

export default OrderModalFooter;
